import { Injectable } from '@angular/core';

export interface MobilePayData {
  gatewayId: string;
  merchantId: string;
  merchantName: string;
  isGooglePayAvailable: boolean;
  isApplePayAvailable: boolean;
}

export const GATEWAY_TYPES = {
  CHECKOUTLTD: 'checkoutltd',
  PAYCOM: 'paycom',
  DIRECT: 'direct',
} as const;

const MERCHANT_ID_PREFIXES = {
  CHECKOUT: 'pk_',
  PAYCOM: 'mrchnt_',
  DIRECT: 'direct_',
} as const;

/**
 * PSPs that run on the mobile-pay page. APPLE_PAY and GOOGLE_PAY reuse the
 * APP_PAY flow but only show their own button.
 */
export const MOBILE_PAY_PSP_CODES = ['APP_PAY', 'APPLE_PAY', 'GOOGLE_PAY'];

export interface MobilePayDataResponse {
  gatewayMerchantId: string;
  processingCurrency: string;
  processingAmount: string;
  pspCode?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppPayService {
  //example datas for gatewayMerchantId
  //PATTERN {PREFIX}-{NUMERIC_ID}-{MERCHANT_NAME}-Apple:{BOOLEAN}-Google:{BOOLEAN}
  //pk_sbox_ec3dqbemfmovmq3gc3zpteuzjix-12345678901234567890-Demo Merchant-Apple:true-Google:false
  //direct_BFU3B5NebBLmSnsNVF5PeegSkuP+9ONCdnWbSgQzJPapzom15SzELDpubOpP95zORi/ln7TWEgmCHMToDKzzeEI=-12345678901234567890-Demo Merchant-Apple:true-Google:true
  private storageKey = 'mobilePayData';

  setMobilePayData(data: MobilePayDataResponse): void {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  getMobilePayData(): MobilePayDataResponse | null {
    const storedData = localStorage.getItem(this.storageKey);
    return storedData ? JSON.parse(storedData) : null;
  }

  clearMobilePayData(): void {
    localStorage.removeItem(this.storageKey);
  }

  getGatewayId(): string {
    const data = this.getMobilePayData();
    return data?.gatewayMerchantId ? data.gatewayMerchantId.split('-')[0] : '';
  }

  getMerchantId(): string {
    const data = this.getMobilePayData();
    return data?.gatewayMerchantId ? data.gatewayMerchantId.split('-')[1] : '';
  }

  getMerchantName(): string {
    const data = this.getMobilePayData();
    return data?.gatewayMerchantId ? data.gatewayMerchantId.split('-')[2] : '';
  }

  getPspCode(): string {
    return this.getMobilePayData()?.pspCode || 'APP_PAY';
  }

  isApplePayAvailable(): boolean {
    if (this.getPspCode() === 'GOOGLE_PAY') return false;
    //Apple Pay should be available as fallback
    const data = this.getMobilePayData();
    if (!data?.gatewayMerchantId) return true;
    const parts = data.gatewayMerchantId.split('-');
    const applePart = parts.find((part) => part.startsWith('Apple:'));
    if (!applePart) return true;
    const appleValue = applePart.split(':')[1];
    return appleValue === 'false' ? false : true;
  }

  isGooglePayAvailable(): boolean {
    if (this.getPspCode() === 'APPLE_PAY') return false;
    const data = this.getMobilePayData();
    if (!data?.gatewayMerchantId) return false;
    const parts = data.gatewayMerchantId.split('-');
    const googlePart = parts.find((part) => part.startsWith('Google:'));
    return googlePart ? googlePart.split(':')[1] === 'true' : false;
  }

  //Only for DIRECT gateway
  getDirectPublicKey(): string {
    const data = this.getMobilePayData();
    if (!data?.gatewayMerchantId) return '';
    const parts = data.gatewayMerchantId.split('-');
    const publicKeyPart = parts.find((part) => part.startsWith('direct_'));
    if (!publicKeyPart) return '';
    const publicKeyValue = publicKeyPart.split('_')[1];
    return publicKeyValue;
  }

  getGatewayType(): string | null {
    const gatewayMerchantId = this.getMobilePayData()?.gatewayMerchantId;

    if (!gatewayMerchantId) {
      return null;
    } else if (gatewayMerchantId.startsWith(MERCHANT_ID_PREFIXES.CHECKOUT)) {
      return GATEWAY_TYPES.CHECKOUTLTD;
    } else if (gatewayMerchantId.startsWith(MERCHANT_ID_PREFIXES.PAYCOM)) {
      return GATEWAY_TYPES.PAYCOM;
    } else if (gatewayMerchantId.startsWith(MERCHANT_ID_PREFIXES.DIRECT)) {
      return GATEWAY_TYPES.DIRECT;
    } else {
      return null;
    }
  }
}
